import { apiFetchJson } from '../http/apiFetch';
import type {
  ShopifyComplianceCompletePayload,
  ShopifyComplianceCustomerSnapshot,
  ShopifyComplianceOrderItemSnapshot,
  ShopifyComplianceOrderSnapshot,
  ShopifyComplianceRedactPayload,
  ShopifyComplianceRedactResult,
  ShopifyComplianceReport,
  ShopifyComplianceRequest,
  ShopifyComplianceRequestDetail,
  ShopifyComplianceRequestFilters,
} from '../../models/shopifyComplianceRequest';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeRequest(row: unknown): ShopifyComplianceRequest | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  const id = asString(r.id ?? r.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    topic: asString(r.topic ?? r.Topic).trim(),
    shopDomain: asString(r.shopDomain ?? r.ShopDomain).trim(),
    shopId: asOptionalString(r.shopId ?? r.ShopId),
    merchantId: asOptionalString(r.merchantId ?? r.MerchantId),
    status: asString(r.status ?? r.Status).trim(),
    receivedAt: asString(r.receivedAt ?? r.ReceivedAt),
    completedAt: asOptionalString(r.completedAt ?? r.CompletedAt),
    completedBy: asOptionalString(r.completedBy ?? r.CompletedBy),
  };
}

function normalizeRequestDetail(row: unknown): ShopifyComplianceRequestDetail | null {
  const base = normalizeRequest(row);
  if (!base || !row || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  const rawOrderIds = r.orderIds ?? r.OrderIds;
  const orderIds = Array.isArray(rawOrderIds) ? rawOrderIds.map((v) => asString(v)) : [];
  return {
    ...base,
    customerShopifyId: asOptionalString(r.customerShopifyId ?? r.CustomerShopifyId),
    customerEmail: asOptionalString(r.customerEmail ?? r.CustomerEmail),
    customerPhone: asOptionalString(r.customerPhone ?? r.CustomerPhone),
    orderIds,
    payloadJson: asString(r.payloadJson ?? r.PayloadJson),
    notes: asOptionalString(r.notes ?? r.Notes),
  };
}

function normalizeOrderItem(row: unknown): ShopifyComplianceOrderItemSnapshot | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const i = row as Record<string, unknown>;
  return {
    productId: asString(i.productId ?? i.ProductId),
    productName: asOptionalString(i.productName ?? i.ProductName),
    sku: asOptionalString(i.sku ?? i.Sku),
    quantity: asNumber(i.quantity ?? i.Quantity),
  };
}

function normalizeOrderSnapshot(row: unknown): ShopifyComplianceOrderSnapshot | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const o = row as Record<string, unknown>;
  const rawItems = o.items ?? o.Items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeOrderItem).filter((i): i is ShopifyComplianceOrderItemSnapshot => Boolean(i))
    : [];
  return {
    salesOrderId: asString(o.salesOrderId ?? o.SalesOrderId),
    orderNumber: asNumber(o.orderNumber ?? o.OrderNumber),
    status: asString(o.status ?? o.Status),
    shopifyOrderId: asOptionalString(o.shopifyOrderId ?? o.ShopifyOrderId),
    customerFullName: asOptionalString(o.customerFullName ?? o.CustomerFullName),
    customerEmail: asOptionalString(o.customerEmail ?? o.CustomerEmail),
    customerPhone: asOptionalString(o.customerPhone ?? o.CustomerPhone),
    customerCompanyName: asOptionalString(o.customerCompanyName ?? o.CustomerCompanyName),
    shippingAddressLine1: asOptionalString(o.shippingAddressLine1 ?? o.ShippingAddressLine1),
    shippingCity: asOptionalString(o.shippingCity ?? o.ShippingCity),
    shippingState: asOptionalString(o.shippingState ?? o.ShippingState),
    shippingPostalCode: asOptionalString(o.shippingPostalCode ?? o.ShippingPostalCode),
    shippingCountry: asOptionalString(o.shippingCountry ?? o.ShippingCountry),
    billingAddressLine1: asOptionalString(o.billingAddressLine1 ?? o.BillingAddressLine1),
    billingCity: asOptionalString(o.billingCity ?? o.BillingCity),
    billingState: asOptionalString(o.billingState ?? o.BillingState),
    billingPostalCode: asOptionalString(o.billingPostalCode ?? o.BillingPostalCode),
    billingCountry: asOptionalString(o.billingCountry ?? o.BillingCountry),
    items,
  };
}

function normalizeCustomerSnapshot(row: unknown): ShopifyComplianceCustomerSnapshot | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const c = row as Record<string, unknown>;
  return {
    customerId: asString(c.customerId ?? c.CustomerId),
    fullName: asOptionalString(c.fullName ?? c.FullName),
    email: asOptionalString(c.email ?? c.Email),
    phone: asOptionalString(c.phone ?? c.Phone),
  };
}

function normalizeReport(row: unknown): ShopifyComplianceReport {
  const r = (row && typeof row === 'object' ? (row as Record<string, unknown>) : {}) as Record<string, unknown>;
  const rawOrders = r.orders ?? r.Orders;
  const rawCustomers = r.customers ?? r.Customers;
  return {
    requestId: asString(r.requestId ?? r.RequestId),
    merchantId: asOptionalString(r.merchantId ?? r.MerchantId),
    orders: Array.isArray(rawOrders)
      ? rawOrders.map(normalizeOrderSnapshot).filter((o): o is ShopifyComplianceOrderSnapshot => Boolean(o))
      : [],
    customers: Array.isArray(rawCustomers)
      ? rawCustomers.map(normalizeCustomerSnapshot).filter((c): c is ShopifyComplianceCustomerSnapshot => Boolean(c))
      : [],
  };
}

function normalizeRedactResult(row: unknown): ShopifyComplianceRedactResult {
  const r = (row && typeof row === 'object' ? (row as Record<string, unknown>) : {}) as Record<string, unknown>;
  return {
    requestId: asString(r.requestId ?? r.RequestId),
    status: asString(r.status ?? r.Status),
    ordersRedacted: asNumber(r.ordersRedacted ?? r.OrdersRedacted),
    customersRedacted: asNumber(r.customersRedacted ?? r.CustomersRedacted),
    completedAt: asOptionalString(r.completedAt ?? r.CompletedAt),
    completedBy: asOptionalString(r.completedBy ?? r.CompletedBy),
  };
}

export async function fetchShopifyComplianceRequests(
  getAccessToken: () => string | undefined,
  filters?: ShopifyComplianceRequestFilters
): Promise<ShopifyComplianceRequest[]> {
  const params = new URLSearchParams();
  if (filters?.topic?.trim()) {
    params.set('topic', filters.topic.trim());
  }
  if (filters?.status?.trim()) {
    params.set('status', filters.status.trim());
  }
  const query = params.toString();
  const path = `/api/admin/shopify-compliance-requests${query ? `?${query}` : ''}`;

  const result = await apiFetchJson<unknown>(path, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  const rows = Array.isArray(result) ? result : [];
  return rows.map(normalizeRequest).filter((r): r is ShopifyComplianceRequest => Boolean(r));
}

export async function fetchShopifyComplianceRequest(
  id: string,
  getAccessToken: () => string | undefined
): Promise<ShopifyComplianceRequestDetail | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/shopify-compliance-requests/${encodeURIComponent(id)}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  return normalizeRequestDetail(result);
}

export async function fetchShopifyComplianceReport(
  id: string,
  getAccessToken: () => string | undefined
): Promise<ShopifyComplianceReport> {
  const result = await apiFetchJson<unknown>(`/api/admin/shopify-compliance-requests/${encodeURIComponent(id)}/report`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  return normalizeReport(result);
}

export async function redactShopifyComplianceRequest(
  id: string,
  payload: ShopifyComplianceRedactPayload,
  getAccessToken: () => string | undefined
): Promise<ShopifyComplianceRedactResult> {
  const result = await apiFetchJson<unknown>(`/api/admin/shopify-compliance-requests/${encodeURIComponent(id)}/redact`, {
    method: 'POST',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeRedactResult(result);
}

export async function completeShopifyComplianceRequest(
  id: string,
  payload: ShopifyComplianceCompletePayload,
  getAccessToken: () => string | undefined
): Promise<ShopifyComplianceRequestDetail | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/shopify-compliance-requests/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeRequestDetail(result);
}
